import dayjs from "dayjs";
import { getSeasonPhase, getSeasonWorkoutGuidance } from "../lib/seasonProfile";
import { buildSwotPriorityPayload } from "../lib/swotPriorityRouter";

export const generateSCProgram = async ({
  userId,
  program,
  numWorkouts,
  trainingDays,
  workoutLength, // <--- Added this (passed from button)
  modeledPlayer,
  supabase,
  setStatusMessage
}) => {
  const mergeAthleticSwotPayloads = (payloads) => {
    const valid = (Array.isArray(payloads) ? payloads : []).filter(Boolean);
    if (valid.length === 0) return null;

    const prioritiesByKey = new Map();
    const categories = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    const categorySeen = {
      strengths: new Set(),
      weaknesses: new Set(),
      opportunities: new Set(),
      threats: new Set(),
    };

    valid.forEach((payload) => {
      (payload.athletic_priorities || []).forEach((entry) => {
        const key = String(entry?.id || entry?.item || "").trim();
        if (!key || prioritiesByKey.has(key)) return;
        prioritiesByKey.set(key, entry);
      });
      const athleticCategories = payload.athletic_categories || {};
      ["strengths", "weaknesses", "opportunities", "threats"].forEach((category) => {
        (athleticCategories[category] || []).forEach((entry) => {
          const key = String(entry?.id || `${category}:${entry?.item || ""}`).trim();
          if (!key || categorySeen[category].has(key)) return;
          categorySeen[category].add(key);
          categories[category].push(entry);
        });
      });
    });

    const athleticPriorities = Array.from(prioritiesByKey.values());
    return {
      athletic_priorities: athleticPriorities,
      athletic_priority_focus_items: athleticPriorities.map((entry) => entry?.item).filter(Boolean),
      athletic_categories: categories
    };
  };

  const today = dayjs().format("YYYY-MM-DD");
  const seasonPhase = getSeasonPhase(program);
  const seasonGuidance = getSeasonWorkoutGuidance(seasonPhase);
  const requestedWorkoutLength = Number(workoutLength);
  const effectiveWorkoutLength = Number.isFinite(requestedWorkoutLength) && requestedWorkoutLength > 0
    ? requestedWorkoutLength
    : seasonGuidance.recommendedWorkoutLength;
  const normalizedTrainingDays = Array.isArray(trainingDays)
    ? [...new Set(trainingDays.map((d) => Number(d)).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))]
    : [];

  const buildTrainingDates = (sessionCount) => {
    if (!normalizedTrainingDays.length || sessionCount <= 0) return [];

    const start = new Date();
    const todayDow = start.getDay();
    const ordered = normalizedTrainingDays
      .map((dow) => ({ dow, delta: (dow - todayDow + 7) % 7 }))
      .sort((a, b) => a.delta - b.delta);

    const dates = [];
    let weekOffset = 0;

    while (dates.length < sessionCount) {
      for (const slot of ordered) {
        if (dates.length >= sessionCount) break;
        const d = new Date(start);
        d.setDate(start.getDate() + slot.delta + (weekOffset * 7));
        dates.push(d);
      }
      weekOffset += 1;
    }

    return dates;
  };
  setStatusMessage("Checking program requirements...");

  try {
    // --- 1. Fetch Auth Token ---
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Authentication required. Please log in.");
    const token = session.access_token;

    let swotPriorityPayload = null;
    const { data: swotRows, error: swotError } = await supabase
      .from("athlete_swot")
      .select("id, program_id, position_id, strengths, weaknesses, opportunities, threats, updated_at, favorited")
      .eq("athlete_id", userId)
      .eq("program_id", program.id)
      .eq("status", "published")
      .order("favorited", { ascending: false })
      .order("updated_at", { ascending: false });
    if (swotError) throw swotError;
    const sharedAthleticSwot = (swotRows || []).find((row) => !row?.position_id) || null;
    if (sharedAthleticSwot) {
      swotPriorityPayload = buildSwotPriorityPayload(sharedAthleticSwot, { sport: program?.sport || "" });
    } else {
      // Backward compatibility for older data where athletic priorities may still live in position rows.
      const athleticPayloads = (swotRows || []).map((row) => (
        buildSwotPriorityPayload(row, { sport: program?.sport || "" })
      ));
      swotPriorityPayload = mergeAthleticSwotPayloads(athleticPayloads);
    }

    // --- 2. Fetch Athlete Demographics + Scores ---
    setStatusMessage("Analyzing athlete profile...");

    const { data: athleteRow, error: athleteError } = await supabase
      .from("users")
      .select("age, grade")
      .eq("id", userId)
      .maybeSingle();
    if (athleteError) throw athleteError;
    const athleteAge = Number(athleteRow?.age);
    const athleteGrade = Number(athleteRow?.grade);
    const resolvedAge = Number.isFinite(athleteAge) && athleteAge > 0 ? athleteAge : null;
    const resolvedGrade = Number.isFinite(athleteGrade) && athleteGrade > 0 ? athleteGrade : null;

    // Fetch Program Goals
    const { data: programGoalScores, error: programError } = await supabase
      .from("program_goal_combine_scores")
      .select("drill_name, target_score")
      .eq("program_id", program.id);

    if (programError) throw programError;

    // Fetch User Scores
    const { data: userCombineScores, error: userCombineError } = await supabase
      .from("user_combine_score")
      .select("user_score, program_combine_scores(drill_name, target_score)")
      .eq("athlete_id", userId)
      .eq("program_id", program.id);

    if (userCombineError) throw userCombineError;

    // --- 3. Monthly Overview (Find or Create) ---
    setStatusMessage("Setting up monthly cycle...");

    let overviewRow;
    const { data: existingOverview } = await supabase
      .from("user_exercise_programs")
      .select("*")
      .eq("athlete_id", userId)
      .eq("followed_program", program.id)
      .lte("start_date", today)
      .gte("end_date", today)
      .limit(1);

    if (existingOverview && existingOverview.length > 0) {
      overviewRow = existingOverview[0];
    } else {
      // Create New
      const athleteName = modeledPlayer?.customLabel || modeledPlayer || "Unknown";

      const newOverview = {
        start_date: today,
        end_date: dayjs().add(1, "month").format("YYYY-MM-DD"),
        followed_program: program.id,
        modeled_athlete: athleteName,
        // Safety check: ensure we don't stringify 'null'
        program_combine_scores: JSON.stringify(programGoalScores || []),
        user_combine_scores: JSON.stringify(userCombineScores || []),
        training_frequency: Number(numWorkouts),
        athlete_id: userId,
        sport: program.sport,
      };

      const { data: inserted, error: insertError } = await supabase
        .from("user_exercise_programs")
        .insert([newOverview])
        .select()
        .single();

      if (insertError) throw insertError;
      overviewRow = inserted;
    }

    // --- 4. Check for Existing Workouts ---
    // If user hits generate again, don't charge AI costs if data exists
    const { data: existingWorkouts } = await supabase
      .from("daily_exercise_drills")
      .select("*")
      .eq("user_id", userId)
      .eq("followed_program", program.id)
      .gte("drill_date", today)
      .limit(1);

    if (existingWorkouts && existingWorkouts.length > 0) {
      setStatusMessage("Workouts found! Loading...");
      return {
        overview: overviewRow,
        workouts: existingWorkouts,
        isNew: false
      };
    }

    // --- 5. Generate Week 1 (Edge Function) ---
    setStatusMessage("AI is designing Week 1...");

    const res1 = await fetch(`https://kzklgxnbtquhjfywtnsz.functions.supabase.co/generateExercisePrograms`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        exercise_program_id: overviewRow.id,
        age: resolvedAge,
        grade: resolvedGrade,
        current_age: resolvedAge,
        user_grade: resolvedGrade,
        target_age: Number(program?.player_peaking) || 18,
        season_phase: seasonGuidance.phase,
        season_guidance: seasonGuidance.emphasis,
        workout_intensity_target: seasonGuidance.intensity,
        workout_length: effectiveWorkoutLength,
        training_days: normalizedTrainingDays,
        swot_priorities_athletic: swotPriorityPayload?.athletic_priorities || [],
        athletic_priority_focus_items: swotPriorityPayload?.athletic_priority_focus_items || [],
        swot_athletic_categories: swotPriorityPayload?.athletic_categories || null
      }),
    });

    if (!res1.ok) {
      const errorData = await res1.json();
      throw new Error(errorData.error || "Failed to generate base program");
    }

    const edgeData = await res1.json();
    const weeklyPlans = edgeData?.days || [];
    const plannedSessionDates = buildTrainingDates(weeklyPlans.length);

    // Insert Week 1 Workouts
    const newToday = new Date();
    const workoutRows = [];

    weeklyPlans.forEach((day, dayIndex) => {
      const drillDate = plannedSessionDates[dayIndex] || new Date(newToday);
      if (!plannedSessionDates[dayIndex]) {
        drillDate.setDate(newToday.getDate() + ((day.day || (dayIndex + 1)) - 1));
      }

      day.exercises.forEach(ex => {
        workoutRows.push({
          exercise_program_id: overviewRow.id,
          drill_name: ex.drill_name,
          score: ex.score,
          drill_date: drillDate.toISOString().split("T")[0],
          description: ex.description,
          notes: ex.notes || "",
          followed_program: program.id,
          user_id: userId,
          number_week: 1,
          duration_or_reps: ex.duration_or_reps,
          order_index: ex.order_index
        });
      });
    });

    if (workoutRows.length > 0) {
      const { data: insertedWeeks, error: weekError } = await supabase
        .from("daily_exercise_drills")
        .insert(workoutRows)
        .select();

      if (weekError) throw weekError;

      // --- 6. Generate Weeks 2-4 (Edge Function) ---
      setStatusMessage("AI is designing Weeks 2-4...");

      // Fire and forget (or await if you want to be sure)
      await fetch(`https://kzklgxnbtquhjfywtnsz.functions.supabase.co/generate_strength_progression`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          exercise_program_id: overviewRow.id,
          age: resolvedAge,
          grade: resolvedGrade,
          current_age: resolvedAge,
          user_grade: resolvedGrade,
          target_age: Number(program?.player_peaking) || 18,
          season_phase: seasonGuidance.phase,
          season_guidance: seasonGuidance.emphasis,
          workout_intensity_target: seasonGuidance.intensity,
          workout_length: effectiveWorkoutLength,
          training_days: normalizedTrainingDays,
          swot_priorities_athletic: swotPriorityPayload?.athletic_priorities || [],
          athletic_priority_focus_items: swotPriorityPayload?.athletic_priority_focus_items || [],
          swot_athletic_categories: swotPriorityPayload?.athletic_categories || null
        })
      });

      setStatusMessage("Program Ready!");

      return {
        overview: overviewRow,
        workouts: insertedWeeks,
        isNew: true
      };
    } else {
      throw new Error("AI returned no workouts.");
    }

  } catch (err) {
    console.error("SC Generator Error:", err);
    throw err;
  }
};
